"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ClipboardCheck, ShieldCheck, Wrench, MessageSquare, ArrowRight, Calendar, Building2, HardHat } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function CaseHighlightImageConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(p => p.title === "Projects Case Highlight Commercial Image" || p.customFields?.photo_name === "Projects Case Highlight Commercial Image");
  const photoUrl = matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage;
  const photoAlt = matchedPhoto?.customFields?.alt_text || "Commercial scaffolding structure erected to TG20:21 safety standards";
  if (photoUrl) {
    const dynamicImage = {
      src: photoUrl,
      srcset: "",
      sizes: ""
    };
    return <img src={dynamicImage.src} data-wvc-srcset={dynamicImage.srcset} data-wvc-sizes={dynamicImage.sizes} alt={photoAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/case_study_commercial_reading.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/case_study_commercial_reading.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/case_study_commercial_reading.png-wvc-sizes" alt="Commercial scaffolding structure erected to TG20:21 safety standards" loading="lazy" decoding="async" className="w-full h-full object-cover" />;
}
export default function CaseHighlight() {
  const shouldReduceMotion = useReducedMotion();
  const sitePhotoQuery = React.useMemo(() => new WP_Query({
    post_type: "site_photo",
    per_page: 50
  }), []);
  const processSteps = [{
    label: "Stage 01",
    icon: ClipboardCheck,
    title: "Initial Site Assessment & Scoping",
    description: "We evaluate site dimensions, ground conditions, pedestrian interfaces, and access constraints to determine the exact specification required for your build.",
    tag: "Site Survey"
  }, {
    label: "Stage 02",
    icon: ShieldCheck,
    title: "Engineering & Safety Planning",
    description: "Every structure is prepared in line with TG20:21 guidelines or bespoke design calculations, paired with comprehensive Risk Assessments and Method Statements (RAMS).",
    tag: "TG20:21 & RAMS"
  }, {
    label: "Stage 03",
    icon: Wrench,
    title: "Erection, Inspection & Handover",
    description: "Our CISRS-qualified scaffolders build to specification, completing a formal handover certificate and tagging the scaffold for immediate, safe trade use.",
    tag: "Handover Certificate"
  }, {
    label: "Stage 04",
    icon: MessageSquare,
    title: "Ongoing Support & Safe Dismantle",
    description: "We provide statutory 7-day safety inspections throughout the hire period, followed by prompt, controlled dismantling when your works are complete.",
    tag: "Full Cycle Support"
  }];
  const workingStandards = [{
    label: "Accreditation",
    value: "NASC & CHAS Verified",
    icon: ShieldCheck
  }, {
    label: "Standard",
    value: "TG20:21 & BS EN 12811",
    icon: HardHat
  }, {
    label: "Workforce",
    value: "CISRS Qualified Team",
    icon: Building2
  }, {
    label: "Inspections",
    value: "Statutory 7-Day Audits",
    icon: Calendar
  }];
  return <section id="case-study-highlight" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="w-6 h-[3px] bg-primary block" />
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Working Standards
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground tracking-tight leading-tight">
            How We Approach a Project
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            From initial site scoping to final dismantle, our structured delivery process ensures every scaffold is engineered for safety, compliance, and trade efficiency.
          </p>
        </div>

        {/* Main Grid: Visual Media + Structured Technical Spec */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Illustrative Photography & Standards */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative rounded-lg overflow-hidden border border-border bg-muted shadow-sm group">
              <div className="aspect-[4/5] sm:aspect-[16/11] lg:aspect-[4/5] w-full overflow-hidden">
                <PostsProvider wp_query={sitePhotoQuery}>
                  <CaseHighlightImageConsumer />
                </PostsProvider>
              </div>
              <div className="p-4 bg-card border-t border-border">
                <p className="text-xs text-muted-foreground font-medium">
                  Illustrative commercial access structure with full perimeter guardrails, debris netting, and secured access bays.
                </p>
              </div>
            </div>

            {/* Operational Standards Box */}
            <div className="grid grid-cols-2 gap-3">
              {workingStandards.map((spec, i) => {
              const IconComponent = spec.icon;
              return <div key={spec.label} data-index={i} className="p-3.5 rounded-lg border border-border bg-card shadow-2xs flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <IconComponent className="size-4 text-primary shrink-0" />
                      <span className="text-[11px] font-semibold uppercase tracking-wider">
                        {spec.label}
                      </span>
                    </div>
                    <span className="text-xs md:text-sm font-bold text-foreground">
                      {spec.value}
                    </span>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Right Column: 4 Process Breakdown Cards */}
          <div className="lg:col-span-7 flex flex-col gap-4">
            {processSteps.map((item, i) => {
            const IconComponent = item.icon;
            return <motion.div key={item.label} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.15
            }} transition={{
              duration: 0.45,
              delay: shouldReduceMotion ? 0 : i * 0.08,
              ease: "easeOut"
            }}>
                  <Card className="border border-border bg-card shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
                    <CardContent className="p-5 md:p-6">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex items-center gap-2.5">
                          <div className="size-8 rounded-md bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                            <IconComponent className="size-4 text-primary" />
                          </div>
                          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            {item.label}
                          </span>
                        </div>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                          {item.tag}
                        </span>
                      </div>

                      <h3 className="text-base md:text-lg font-bold text-foreground mt-1 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}

            {/* Action Bar */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.45,
            delay: 0.35,
            ease: "easeOut"
          }} className="mt-4 p-5 rounded-lg border border-border bg-muted/40 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <h4 className="text-sm font-bold text-foreground">
                  Planning a commercial or residential scheme?
                </h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Speak directly with our contracts team for site surveys and technical quotes.
                </p>
              </div>

              <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
                <Button asChild className="w-full sm:w-auto">
                  <Link to="/ContactRequestQuotePage">
                    Request a Quote
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/CommercialScaffoldingPage">
                    Commercial Services
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}